import { useState } from "react";
import { Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, homeFor } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { Logo } from "../components/Brand";
import { Field } from "../components/common";

export default function AuthPage({ mode }) {
  const { user, login, register } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", location: "", company_name: "", role: params.get("role") === "employer" ? "employer" : "staff" });
  const [busy, setBusy] = useState(false);
  const isReg = mode === "register";
  const next = (params.get("next") || "").startsWith("/app/") ? params.get("next") : "";
  const qs = params.toString() ? `?${params.toString()}` : "";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const dest = (role) => (next && role === "staff" ? next : homeFor(role));

  if (user) return <Navigate to={dest(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = isReg ? await register(form) : await login(form.email, form.password);
      toast.success(`Welcome, ${u.name}`);
      navigate(dest(u.role));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative dx-grain bg-[#250E31] text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: "url(/brand/training_cssbb.jpg)" }} />
        <Link to="/" className="relative"><Logo light /></Link>
        <div className="relative">
          <div className="dx-eyebrow">Dexterity Consult</div>
          <h2 className="text-4xl font-bold mt-4 leading-tight">The ability to learn and translate knowledge into solving problems is the ultimate competitive advantage.</h2>
          <p className="text-white/60 mt-4 text-sm">- Jack Welch</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-5 dx-rise" data-testid={`${mode}-form`}>
          <Link to="/" className="lg:hidden inline-block mb-4"><Logo /></Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-[#250E31]">{isReg ? "Create your account" : "Welcome back"}</h1>
            <p className="text-sm text-[#6b5a73] mt-2">{next.includes("enrol=") ? "Create your staff account to start enrolling - you'll be taken straight to your course next." : isReg ? "Join as staff to train and get hired, or as an employer to hire." : "Log in to continue."}</p>
          </div>
          {isReg && (
            <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-[#F4EEF7]">
              {["staff", "employer"].map((r) => (
                <button type="button" key={r} onClick={() => setForm({ ...form, role: r })} data-testid={`role-${r}-btn`}
                  className={`rounded-full py-2 text-sm font-semibold capitalize transition-colors ${form.role === r ? "bg-[#5B2A6E] text-white" : "text-[#5B2A6E]"}`}>
                  {r === "staff" ? "I want a job" : "I'm hiring"}
                </button>
              ))}
            </div>
          )}
          {isReg && <Field label="Full name"><input className="dx-input" required value={form.name} onChange={set("name")} data-testid="register-name-input" /></Field>}
          {isReg && form.role === "employer" && <Field label="Company name"><input className="dx-input" required value={form.company_name} onChange={set("company_name")} data-testid="register-company-input" /></Field>}
          <Field label="Email"><input type="email" className="dx-input" required value={form.email} onChange={set("email")} data-testid={`${mode}-email-input`} /></Field>
          <Field label="Password"><input type="password" className="dx-input" required minLength={6} value={form.password} onChange={set("password")} data-testid={`${mode}-password-input`} /></Field>
          {isReg && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone"><input className="dx-input" value={form.phone} onChange={set("phone")} placeholder="Mobile number" data-testid="register-phone-input" /></Field>
              <Field label="Town / City"><input className="dx-input" value={form.location} onChange={set("location")} placeholder="City" data-testid="register-location-input" /></Field>
            </div>
          )}
          <button className="dx-btn-gold w-full py-3" disabled={busy} data-testid={`${mode}-submit-btn`}>{busy ? "Please wait…" : isReg ? "Create account" : "Log in"}</button>
          <p className="text-sm text-center text-[#6b5a73]">
            {isReg ? "Already have an account? " : "New here? "}
            <Link to={`${isReg ? "/login" : "/register"}${qs}`} className="font-semibold text-[#5B2A6E]" data-testid="auth-switch-link">{isReg ? "Log in" : "Create an account"}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
